import { Link } from 'react-router'
import { CalendarClock } from 'lucide-react'
import type { ProposalFollowUpDto } from '@lib/dto/proposal-follow-up.dto'
import { formatDate } from '@lib/helpers/format'
import { formatMoney, formatMoneyAmount, parseMoney } from '@lib/helpers/money'
import {
  COL_OPTIONAL,
  COL_SECONDARY,
  COL_STICKY,
  ROW,
  TABLE,
  TABLE_FRAME,
  TD,
  TD_RIGHT,
  TH,
  TH_RIGHT,
} from '@/components/ui/table-classes'
import { cn } from '@/lib/utils'

/**
 * How the deferred work reads. A two-year plan chose to leave it for next
 * year; anything else was set aside, which is a softer lead.
 */
function deferredLabel(followUp: ProposalFollowUpDto): string {
  return followUp.plan === 'PHASED' ? 'Planned for next year' : 'Left for later'
}

function totalDeferred(followUps: ProposalFollowUpDto[]): string {
  const cents = followUps.reduce(
    (sum, followUp) => sum + (parseMoney(followUp.deferredAmount) ?? 0),
    0,
  )
  return formatMoney(cents)
}

/** The list itself, separate from loading so it can be tested on data. */
export function ProposalFollowUpTable({
  followUps,
}: {
  followUps: ProposalFollowUpDto[]
}) {
  if (followUps.length === 0) return null

  return (
    <section className="space-y-2" aria-labelledby="follow-ups-heading">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2
          id="follow-ups-heading"
          className="flex items-center gap-1.5 text-sm font-semibold"
        >
          <CalendarClock className="h-3.5 w-3.5 text-muted-text" />
          Deferred work to follow up
        </h2>
        <p className="text-xs text-muted-text">
          {followUps.length} {followUps.length === 1 ? 'proposal' : 'proposals'}{' '}
          ·{' '}
          <span className="tabular-nums">
            {totalDeferred(followUps)}
          </span>{' '}
          surveyed and priced, before tax
        </p>
      </div>
      <div className={TABLE_FRAME}>
        <table className={TABLE}>
          <thead>
            <tr className="border-b border-default text-secondary-text">
              <th className={cn(TH, COL_STICKY, 'bg-surface')}>Client</th>
              <th className={cn(TH, COL_SECONDARY)}>Proposal</th>
              <th className={TH}>Deferred</th>
              <th className={cn(TH, COL_OPTIONAL)}>Accepted</th>
              <th className={TH_RIGHT}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {followUps.map((followUp) => (
              <tr
                key={followUp.versionId}
                className={cn(ROW, 'border-b border-default last:border-b-0')}
              >
                <td className={cn(TD, COL_STICKY)}>
                  <Link
                    to={`/proposals/${followUp.proposalId}`}
                    className="font-medium hover:text-brand hover:underline"
                  >
                    {followUp.clientName}
                  </Link>
                </td>
                <td className={cn(TD, COL_SECONDARY, 'text-secondary-text')}>
                  {followUp.title}
                </td>
                <td className={cn(TD, 'text-secondary-text')}>
                  {deferredLabel(followUp)}
                </td>
                <td className={cn(TD, COL_OPTIONAL, 'text-secondary-text')}>
                  {formatDate(followUp.acceptedAt)}
                </td>
                <td className={cn(TD_RIGHT, 'tabular-nums')}>
                  {formatMoneyAmount(followUp.deferredAmount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
